"use client";

import { HeroSlider } from "@/components/hyper-or/HeroSlider";

export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-ink text-cream">
      {/* Subtle texture layer behind hero text content */}
      <div className="absolute inset-0 ink-gradient pointer-events-none"></div>
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(200,162,74,0.45) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(200,162,74,0.25) 0%, transparent 50%)",
        }}
      ></div>

      {/* Dedicated luxury slider band at the top */}
      <div className="relative w-full h-[340px] sm:h-[420px] md:h-[500px] lg:h-[560px] bg-ink">
        <HeroSlider />
      </div>

      {/* Marquee univers — exactement comme l'original */}
      <div className="relative border-b border-cream/10 py-4 overflow-hidden">
        <div className="flex items-center justify-center flex-wrap gap-x-8 gap-y-2 px-6 text-center">
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            Frais &amp; Marché
          </span>
          <span className="text-gold text-xl">✦</span>
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            Épicerie Fine
          </span>
          <span className="text-gold text-xl">✦</span>
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            Beauté &amp; Parfums
          </span>
          <span className="text-gold text-xl">✦</span>
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            High-Tech &amp; Électro
          </span>
          <span className="text-gold text-xl">✦</span>
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            Cave d&apos;Exception
          </span>
          <span className="text-gold text-xl">✦</span>
          <span className="font-display italic text-cream/85 text-xl md:text-2xl px-4">
            Mode &amp; Maison
          </span>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: hero text */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-gold"></span>
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                Paris · France · depuis 2019
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight">
              L&apos;art de la{" "}
              <span className="gold-text italic">grande distribution</span>
              <br />
              sublimé.
            </h1>

            <p className="mt-8 text-cream/70 text-lg md:text-xl font-serif leading-relaxed max-w-2xl">
              Bienvenue à{" "}
              <span className="text-gold italic">Hyper OR I</span>,
              l&apos;hypermarché de référence par{" "}
              <span className="text-gold italic">SENE &amp; DIA</span>. Une
              expérience d&apos;achat d&apos;exception, entre marbre blanc,
              laiton et savoir-vivre parisien.{" "}
              <span className="text-gold italic">SmarTest Capital.</span>
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#categories"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full gold-gradient text-ink font-semibold tracking-[0.2em] uppercase text-sm gold-shadow hover:scale-[1.02] active:scale-[0.98] transition-transform"
              >
                Explorer les boutiques
                <svg
                  className="transition-transform group-hover:translate-x-1"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </a>
              <a
                href="#experience"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-cream/30 text-cream hover:border-gold hover:text-gold transition-colors tracking-[0.2em] uppercase text-sm"
              >
                Visite virtuelle
              </a>
            </div>

            {/* Stats strip — valeurs exactes du site original */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 border-t border-cream/10 pt-8">
              <div>
                <div className="font-display text-gold text-3xl md:text-4xl">
                  40 000+
                </div>
                <div className="text-cream/50 text-[10px] tracking-[0.25em] uppercase mt-1">
                  Références
                </div>
              </div>
              <div>
                <div className="font-display text-gold text-3xl md:text-4xl">
                  12
                </div>
                <div className="text-cream/50 text-[10px] tracking-[0.25em] uppercase mt-1">
                  Boutiques
                </div>
              </div>
              <div>
                <div className="font-display text-gold text-3xl md:text-4xl">
                  08h–22h
                </div>
                <div className="text-cream/50 text-[10px] tracking-[0.25em] uppercase mt-1">
                  7j/7
                </div>
              </div>
              <div>
                <div className="font-display text-gold text-3xl md:text-4xl">
                  150 K
                </div>
                <div className="text-cream/50 text-[10px] tracking-[0.25em] uppercase mt-1">
                  Clients/mois
                </div>
              </div>
            </div>
          </div>

          {/* Right: emblematic card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl border border-gold/20 rotate-2"></div>
              <div className="absolute -inset-3 rounded-3xl border border-gold/40 -rotate-1"></div>
              <div className="relative bg-gradient-to-br from-ink/40 to-ink/80 backdrop-blur-sm rounded-3xl border border-gold/30 p-8 md:p-10 luxury-shadow">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-gold text-xs tracking-[0.3em] uppercase">
                    Carte d&apos;or
                  </span>
                  <span className="font-display italic text-gold text-2xl">
                    OR
                  </span>
                </div>

                <div className="space-y-5">
                  <div className="flex items-center justify-between pb-4 border-b border-cream/10">
                    <div>
                      <div className="font-display italic text-cream/85 text-xl">
                        Frais &amp; Marché
                      </div>
                      <div className="text-cream/40 text-xs mt-0.5">
                        Le Terroir Français · 50 produits
                      </div>
                    </div>
                    <div className="text-gold text-2xl">❖</div>
                  </div>
                  <div className="flex items-center justify-between pb-4 border-b border-cream/10">
                    <div>
                      <div className="font-display italic text-cream/85 text-xl">
                        Cave d&apos;Exception
                      </div>
                      <div className="text-cream/40 text-xs mt-0.5">
                        Vins &amp; Spiritueux · 800 références
                      </div>
                    </div>
                    <div className="text-gold text-2xl">❦</div>
                  </div>
                  <div className="flex items-center justify-between pb-4 border-b border-cream/10">
                    <div>
                      <div className="font-display italic text-cream/85 text-xl">
                        Beauté &amp; Parfums
                      </div>
                      <div className="text-cream/40 text-xs mt-0.5">
                        Grands Parfumeurs · 50 marques
                      </div>
                    </div>
                    <div className="text-gold text-2xl">❀</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display italic text-cream/85 text-xl">
                        High-Tech &amp; Électro
                      </div>
                      <div className="text-cream/40 text-xs mt-0.5">
                        Galerie Numérique · 50 produits
                      </div>
                    </div>
                    <div className="text-gold text-2xl">◈</div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gold/20 flex items-center justify-between">
                  <span className="text-cream/50 text-[10px] tracking-[0.3em] uppercase">
                    SmarTest Capital
                  </span>
                  <span className="font-serif italic text-gold text-lg">
                    SENE &amp; DIA
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll cue */}
      <div className="relative border-t border-cream/10 py-4">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between text-cream/50 text-[11px] tracking-[0.25em] uppercase">
          <span>SmarTest Capital · MMXIX</span>
          <a
            href="#categories"
            className="inline-flex items-center gap-2 hover:text-gold transition-colors"
          >
            Scroll
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14"></path>
              <path d="m19 12-7 7-7-7"></path>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
